/**
 * BM Versioning MVI Effects
 *
 * Architecture: MVI Pattern - Effects Layer
 * All side effects (API calls, localStorage, etc.) are isolated here.
 */

import type { TFeatureId } from "@/shared/types/feature";
import type { ICommitRequest, ICheckoutRequest } from "@/shared/types/bm-versioning";
import { getBmVersioningApiClient } from "@/shared/api";
import { i18n } from "@/shared/i18n";
import { versioningIntents, type TVersioningIntent } from "./intents";

type Dispatch = (intent: TVersioningIntent) => void;

// ============================================================================
// API Client
// ============================================================================

const api = getBmVersioningApiClient();

// ============================================================================
// Effects
// ============================================================================

/**
 * Load commit history for a feature
 */
export async function loadHistory(
  dispatch: Dispatch,
  featureId: TFeatureId
): Promise<void> {
  dispatch(versioningIntents.loadHistoryStart());

  try {
    const history = await api.getHistory(featureId);
    dispatch(versioningIntents.loadHistorySuccess(history));
  } catch (error) {
    dispatch(
      versioningIntents.loadHistoryError({
        message: error instanceof Error ? error.message : i18n.global.t("versioning.loadHistoryError"),
        code: "LOAD_HISTORY_ERROR",
      })
    );
  }
}

/**
 * Create a commit from the current HEAD feature
 *
 * @returns The new feature ID (new HEAD), or null on error
 */
export async function commit(
  dispatch: Dispatch,
  featureId: TFeatureId,
  message?: string
): Promise<TFeatureId | null> {
  dispatch(versioningIntents.commitStart());

  try {
    const request: ICommitRequest = { message };
    const response = await api.commit(featureId, request);

    dispatch(
      versioningIntents.commitSuccess(response.newFeatureId, response.commitInfo)
    );

    return response.newFeatureId;
  } catch (error) {
    dispatch(
      versioningIntents.commitError({
        message: error instanceof Error ? error.message : i18n.global.t("versioning.commitError"),
        code: "COMMIT_ERROR",
      })
    );
    return null;
  }
}

/**
 * Checkout (create branch) from a specific commit
 *
 * @returns The new feature ID (branch), or null on error
 */
export async function checkout(
  dispatch: Dispatch,
  commitFeatureId: TFeatureId,
  branchName?: string
): Promise<TFeatureId | null> {
  dispatch(versioningIntents.checkoutStart(commitFeatureId));

  try {
    const request: ICheckoutRequest = { branchName };
    const response = await api.checkout(commitFeatureId, request);

    dispatch(
      versioningIntents.checkoutSuccess(response.newFeatureId, response.newFeatureName)
    );

    return response.newFeatureId;
  } catch (error) {
    dispatch(
      versioningIntents.checkoutError({
        message: error instanceof Error ? error.message : i18n.global.t("versioning.checkoutError"),
        code: "CHECKOUT_ERROR",
      })
    );
    return null;
  }
}
